import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import axios, { isAxiosError } from 'axios';

/** Mints a fresh idToken, or resolves null when re-authentication is not possible. */
type TokenRefresher = () => Promise<string | null>;

// The umbrella shell owns MSAL and keeps these in sync; microfrontends just use `authAxios` and
// never handle the token themselves, mirroring how the Apollo client works.
let currentIdToken: string | null = null;
let tokenRefresher: TokenRefresher | null = null;

/** Kept in sync with the logged-in user's idToken by the shell. */
const setAuthToken = (idToken: string | null): void => {
    currentIdToken = idToken;
};

const registerTokenRefresher = (refresher: TokenRefresher): void => {
    tokenRefresher = refresher;
};

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
    /** Set once we have replayed a request, so a still-401 replay does not loop. */
    authRetried?: boolean;
}

/**
 * Axios instance that handles auth the way the Apollo client does: it attaches the current
 * BestOne idToken to every request, and on a 401 refreshes the token once and replays the request.
 * Only use it for BestOne backend calls — it must not leak the token to third-party APIs.
 */
const authAxios: AxiosInstance =
    // eslint-disable-next-line import/no-named-as-default-member
    axios.create();

// `jest.mock('axios')` auto-mocks make `create()` return undefined, and the library barrel pulls
// this module into unrelated tests, so only wire the interceptors when there is a real instance.
const authAxiosInstance = authAxios as AxiosInstance | undefined;

// Request: attach the current token, mirroring the Apollo `authLink`.
authAxiosInstance?.interceptors.request.use((config) => {
    if (currentIdToken) {
        config.headers.set('Authorization', `Bearer ${currentIdToken}`);
    }
    return config;
});

// Response: on a 401, refresh once and replay, mirroring the Apollo `errorLink`.
authAxiosInstance?.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
        const config =
            isAxiosError(error) && error.response?.status === 401
                ? (error.config as RetryableRequestConfig | undefined)
                : undefined;

        if (config && !config.authRetried && tokenRefresher) {
            config.authRetried = true;

            const idToken = await tokenRefresher();

            if (idToken) {
                config.headers.set('Authorization', `Bearer ${idToken}`);
                return authAxios(config);
            }
        }

        return Promise.reject(
            error instanceof Error ? error : new Error(String(error))
        );
    }
);

export { authAxios, registerTokenRefresher, setAuthToken };
export type { TokenRefresher };
